import { navigateTo, useRouter } from '#imports'
import { type Ref, readonly, ref } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { closeModal, openModal } from '@ds/components/ModalBase'

import { SHOPPING_ASSISTANT } from '../routes.constants'

import type {
  NewChatDialog,
  WebShoppingAssistantHost,
} from './useShoppingAssistantHost.injection-key'

// Single RevDialog instance shared by the new-chat confirmation.
export const WEB_PAGE_CONFIRM_DIALOG_NAME =
  'shopping-assistant-web-page-confirm'

/**
 * Builds the host adapter for the standalone /assistant web page. Close
 * navigates back to the homepage immediately (no confirmation — this is a
 * full page and the conversation stays in server-side history), new-chat is
 * confirmed via a RevDialog (gated on a non-empty conversation), and
 * cart/url/ready concerns are no-ops on the web (the front-office cart
 * updates reactively in-app; there is no chat URL in v1).
 *
 * Returns the host plus the bits the container component needs to render the
 * confirmation dialog and to inject a message into a live conversation.
 */
export function useWebPageShoppingAssistantHost(): {
  host: WebShoppingAssistantHost
  currentDialog: Ref<NewChatDialog | null>
  acceptDialog: () => void
  declineDialog: () => void
  handleDialogClose: (closingEvent?: { cause?: string }) => void
} {
  const router = useRouter()
  const logger = useLogger()

  const receivedEvent = ref('')
  const hasUserMessages = ref(false)
  const currentDialog = ref<NewChatDialog | null>(null)

  function openConfirmDialog(dialog: NewChatDialog) {
    // Single-slot: drop a new request while one is already pending so the
    // user's in-flight choice (onAccept/onDecline) is never swapped under them.
    if (currentDialog.value) {
      return
    }

    currentDialog.value = dialog
    openModal(WEB_PAGE_CONFIRM_DIALOG_NAME)
  }

  function acceptDialog() {
    const dialog = currentDialog.value
    currentDialog.value = null
    closeModal(WEB_PAGE_CONFIRM_DIALOG_NAME)
    dialog?.onAccept()
  }

  function declineDialog() {
    const dialog = currentDialog.value
    currentDialog.value = null
    closeModal(WEB_PAGE_CONFIRM_DIALOG_NAME)
    dialog?.onDecline?.()
  }

  function handleDialogClose(closingEvent?: { cause?: string }) {
    if (closingEvent?.cause === 'accept') return

    const dialog = currentDialog.value
    if (!dialog) return

    currentDialog.value = null
    dialog.onDecline?.()
  }

  const host: WebShoppingAssistantHost = {
    hostType: 'web-page',
    receivedEvent: readonly(receivedEvent),
    clearReceivedEvent: () => {
      receivedEvent.value = ''
    },
    close() {
      navigateTo('/')
    },
    newChat(dialog) {
      // Plus button: confirm only when there's a real conversation to lose.
      if (hasUserMessages.value) {
        openConfirmDialog(dialog)

        return
      }

      dialog.onAccept()
    },
    reportConversation: (value: boolean) => {
      hasUserMessages.value = value
    },
    // Reflect the freshly created or resumed chat id in the URL without a Vue
    // Router navigation (history.replaceState avoids a remount; preserve
    // history.state per Vue Router docs). URL sync is purely cosmetic — a
    // failure here must never block using the chat, so swallow and log it.
    syncChatId: (id: string) => {
      try {
        const newPath = router.resolve({
          name: SHOPPING_ASSISTANT,
          params: { chatId: id },
        }).href
        window.history.replaceState(window.history.state, '', newPath)
      } catch (error) {
        logger.error('[SHOPPING_ASSISTANT] Failed to sync chat id to URL', {
          error: error as Error,
          owners: ['bot-group-ai-shopping-assistant-front'],
          chatId: id,
          hostType: 'web-page',
        })
      }
    },
  }

  return {
    host,
    currentDialog,
    acceptDialog,
    declineDialog,
    handleDialogClose,
  }
}
